import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import InventoryLayout from "@/components/inventory/InventoryLayout";
import Modal, {
  TextField,
  TextArea,
  PrimaryButton,
  SecondaryButton,
} from "@/components/inventory/Modal";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyForm = { name: "", description: "", parent_id: "" };

export default function CategoriesPage() {
  const { user } = useAuth();
  const canWrite = user && ["admin", "manager"].includes(user.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/categories", {
        params: { q: q || undefined },
      });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const startEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name,
      description: row.description || "",
      parent_id: row.parent_id || "",
    });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        parent_id: form.parent_id || null,
      };
      if (editing) {
        await api.put(`/inventory/categories/${editing.id}`, payload);
        toast.success("Category updated.");
      } else {
        await api.post("/inventory/categories", payload);
        toast.success("Category created.");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Deactivate category "${row.name}"?`)) return;
    try {
      await api.delete(`/inventory/categories/${row.id}`);
      toast.success("Category deactivated.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <InventoryLayout
      title="Categories"
      right={
        canWrite && (
          <PrimaryButton testid="cat-new-btn" onClick={startCreate}>
            + New category
          </PrimaryButton>
        )
      }
    >
      <div className="mb-6 flex gap-3 max-w-md">
        <input
          data-testid="cat-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name…"
          className="flex-1 h-11 px-3 border border-slate-300 bg-white text-sm focus:outline-none focus:border-[#002FA7] transition-colors"
        />
        <SecondaryButton testid="cat-search-btn" onClick={load}>
          Search
        </SecondaryButton>
      </div>

      <div className="border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Name</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Description</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Status</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 w-40" />
            </tr>
          </thead>
          <tbody data-testid="cat-tbody">
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500 text-sm">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500 text-sm">No categories yet.</td></tr>
            ) : (
              items.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-4 py-3 font-medium" data-testid={`cat-row-name-${row.id}`}>{row.name}</td>
                  <td className="px-4 py-3 text-slate-600">{row.description || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs uppercase tracking-[0.2em] font-semibold ${row.is_active ? "text-[#039855]" : "text-slate-400"}`}>
                      {row.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canWrite && (
                      <div className="flex gap-2 justify-end">
                        <button
                          data-testid={`cat-edit-${row.id}`}
                          onClick={() => startEdit(row)}
                          className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 hover:text-[#002FA7] transition-colors"
                        >
                          Edit
                        </button>
                        {row.is_active && (
                          <button
                            data-testid={`cat-delete-${row.id}`}
                            onClick={() => onDelete(row)}
                            className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D92D20] hover:opacity-80 transition-opacity"
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        testid="cat"
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit category" : "New category"}
      >
        <form onSubmit={onSubmit} className="space-y-5">
          <TextField
            label="Name"
            testid="cat-form-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <TextArea
            label="Description"
            testid="cat-form-description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-2">
            <SecondaryButton
              testid="cat-form-cancel"
              type="button"
              onClick={() => setOpen(false)}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton testid="cat-form-submit" type="submit" disabled={submitting}>
              {submitting ? "Saving…" : editing ? "Save changes" : "Create"}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </InventoryLayout>
  );
}
